import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component } from "@angular/core";
import { Select, ofActionSuccessful } from "@ngxs/store";
import { TranslateModule } from "@ngx-translate/core";
import { Observable, combineLatest, filter, map, merge, take, takeUntil, withLatestFrom } from "rxjs";

import { MessageDataComponent } from "@app/shared/components/message-data/message-data.component";
import { DataGridComponent } from "@app/shared/components/smart-data-grid/data-grid/data-grid.component";
import { SmartSearchFormComponent } from "@app/shared/components/smart-search-form/smart-search-form.component";
import * as AppDataAction from "@app/shared/actions/app-data.action";
import { ExcelSheetName, NgxsActionName, NgxsFormPath } from "@app/shared/constants";
import { AppDataState } from "@app/shared/stores/app-data.state";
import { SearchByItem, WebApiResponse, WebApiResponseBase } from "@app/shared/classes/general";
import { PageBasedComponent } from "@app/pages/page.based.component";
import { DrivingBehavior } from "@app/shared/classes/driving-behavior";
import { DrivingBehaviorDataGridCell } from "@app/shared/components/smart-data-grid/data-grid-cell-templates/driving-behavior-data-grid-cell.component";
import { ImportResult } from "@app/shared/classes/import-result";
import { CommonModalDialogComponent } from "@app/shared/components/common-modal-dialog/common-modal-dialog.component";
import { CommonModalDialogOption, ModalDialogButtonType, ModalDialogResult } from "@app/shared/components/common-modal-dialog/common-modal-dialog.constant";
import { DataGridColumnDef, GetDrivingBehaviorSearchByList } from "./driving-behavior.constant";
import { CommonPageFilterSetting, CommonSearchCriteria, CreatePageFilterSettingWithLstChangeOnSort } from "@app/shared/models/common-data.model";
import { SiteConfigHelper } from "@app/shared/utils/site-config.helper";
import { ExcelImportButtonComponent } from "@app/shared/components/excel-import-button/excel-import-button.component";
import { CsvImportType } from "@app/shared/enums";
import { DisableDeleteButtonDirective } from "@app/shared/directive/disable-delete-button.directive";
import { DisableEditButtonDirective } from "@app/shared/directive/disable-edit-button.directive";
import { DrivingBehaviorEditModalDialogComponent } from "@app/shared/components/edit-modal-dialog/driving-behavior-edit-modal-dialog/driving-behavior-edit-modal-dialog.component";

@Component({
  selector: "app-driving-behavior",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DataGridComponent,
    MessageDataComponent,
    CommonModule,
    TranslateModule,
    DisableDeleteButtonDirective,
    DisableEditButtonDirective,
    SmartSearchFormComponent,
    DrivingBehaviorDataGridCell,
    ExcelImportButtonComponent,
  ],
  templateUrl: "./driving-behavior.component.html",
  styleUrl: "./driving-behavior.component.css",
})
export class DrivingBehaviorComponent extends PageBasedComponent {
  dataGridColumnDef = DataGridColumnDef;
  formPath = NgxsFormPath.KPI_DRIVING_BEHAVIOR_SEARCH_FORM;
  selectedItems: DrivingBehavior[] = [];

  dataSource$: Observable<DrivingBehavior[]>;
  drivingBehaviorSearchByList$: Observable<SearchByItem[]>;

  @Select(AppDataState.currentDrivingBehaviorSearchBy) currentDrivingBehaviorSearchBy$: Observable<CommonSearchCriteria[]>;
  @Select(AppDataState.currentPageFilterSetting(NgxsActionName.GET_KPI_DRIVING_BEHAVIOR)) currentPageFilterSetting$: Observable<CommonPageFilterSetting>;

  @Select(AppDataState.taskExecuting(NgxsActionName.GET_KPI_DRIVING_BEHAVIOR)) searching$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_KPI_DRIVING_BEHAVIOR)) response$: Observable<WebApiResponse<DrivingBehavior[]>>;
  @Select(AppDataState.taskError(NgxsActionName.GET_KPI_DRIVING_BEHAVIOR)) errorResponse$: Observable<WebApiResponseBase>;
  @Select(AppDataState.taskError(NgxsActionName.DELETE_KPI_DRIVING_BEHAVIOR)) deleteErrorResponse$: Observable<WebApiResponseBase>;

  @Select(AppDataState.taskExecuting(NgxsActionName.IMPORT_KPI_DRIVING_BEHAVIOR_DATA)) importing$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.IMPORT_KPI_DRIVING_BEHAVIOR_DATA)) importResponse$: Observable<ImportResult[]>;
  @Select(AppDataState.taskError(NgxsActionName.IMPORT_KPI_DRIVING_BEHAVIOR_DATA)) importErrorResponse$: Observable<WebApiResponseBase>;

  constructor() {
    super();
    this.functionName = NgxsActionName.GET_KPI_DRIVING_BEHAVIOR;
  }

  override ngOnInit() {
    super.ngOnInit();
    this.currentDrivingBehaviorSearchBy$.pipe(take(1)).subscribe((searchCriteria) => this.defaultSearchCriteriaSub.next(searchCriteria));
    this.errorMessage$ = merge(this.commonDataLoadErrorMessage$, this.errorResponse$.pipe(map((errorResponse) => this.getErrorMessages([errorResponse]))));

    this.dataSource$ = this.response$.pipe(
      filter(Boolean),
      map((c) => c.data)
    );
    this.totalCount$ = this.response$.pipe(
      filter(Boolean),
      map((c) => c.totalCount)
    );

    var kpis$ = this.kpiAllData$.pipe(
      filter(Boolean),
      map((c) => c.data),
      filter(Boolean),
      map((kpi) => kpi.filter((item) => item.kpiCategory.kpiCategoryId === SiteConfigHelper.GetKpiCategoryIdMapping().DrivingBehavior).flatMap((item) => item))
    );
    this.drivingBehaviorSearchByList$ = combineLatest([this.currentTerminalDropDowns$, this.currentSubRegions$, kpis$, this.userCountry$]).pipe(
      map((c) => GetDrivingBehaviorSearchByList(c))
    );

    this.actions$
      .pipe(ofActionSuccessful(AppDataAction.DeleteKpiDrivingBehavior), withLatestFrom(this.deleteErrorResponse$), takeUntil(this.destroy$))
      .subscribe(([_, deleteErrorResponse]) => this.showResultDialog(deleteErrorResponse, "ml_143948", "ml_143949", this.onSearchWithExistedPageFilter));

    this.actions$
      .pipe(
        ofActionSuccessful(AppDataAction.ImportKpiDrivingBehaviorData),
        withLatestFrom(this.importResponse$, this.importErrorResponse$, this.profileMe$),
        takeUntil(this.destroy$)
      )
      .subscribe(([_, importResponse, importErrorResponse, profileMe]) =>
        this.showImportResult(importResponse as ImportResult[], importErrorResponse, "ml_143952", "ml_143950", profileMe, this.onSearchWithLastModifyPageFilter)
      );
  }

  rowSelected = (data: DrivingBehavior[]) => {
    this.selectedItems = data ?? [];
  };

  override onSearch = () => {
    this.selectedItems = [];
    this.store.dispatch(new AppDataAction.GetKpiDrivingBehavior());
  };

  onSearchWithExistedPageFilter = () => {
    this.selectedItems = [];
    this.store.dispatch(new AppDataAction.GetKpiDrivingBehavior(null));
  };

  onSearchWithLastModifyPageFilter = () => {
    this.selectedItems = [];
    this.store.dispatch(new AppDataAction.GetKpiDrivingBehavior(CreatePageFilterSettingWithLstChangeOnSort(this.functionName)));
  };

  onGetData = (info: CommonPageFilterSetting) => {
    info.functionName = NgxsActionName.GET_KPI_DRIVING_BEHAVIOR;
    this.store.dispatch(new AppDataAction.GetKpiDrivingBehavior(info));
  };

  onEdit = () => {
    this.dialog.open(DrivingBehaviorEditModalDialogComponent, {
      width: "500px",
      data: { data: this.selectedItems[0], successHandler: this.onSearchWithExistedPageFilter },
      disableClose: true,
    });
  };

  onDelete = () => {
    const dialogRef = this.dialog.open(CommonModalDialogComponent, {
      data: { header: true, message: "ml_143933", buttonType: ModalDialogButtonType.YES_NO } as CommonModalDialogOption,
      disableClose: false,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result.data !== ModalDialogResult.YES) {
          return;
        }

        this.store.dispatch(new AppDataAction.DeleteKpiDrivingBehavior(this.selectedItems.map((item) => item.kpiDrivingBehavior_Id)));
      });
  };

  onImport = (file: File) => {
    this.excelImportService.OnFileSlected(
      file,
      [ExcelSheetName.DRIVER_OBSERVATION],
      (fileData) => {
        var importData = this.csvService.GetImportObjectFromCsvData(CsvImportType.DRIVING_BEHAVIOR, fileData);
        if (importData.length === 0) {
          this.showImportEmptyDataResult();
        } else {
          this.store.dispatch(new AppDataAction.ImportKpiDrivingBehaviorData(importData));
        }
      },
      this.showImportEmptyDataResult
    );
  };
}
